/** Sidebar da Agenda: cards 'Agendas', 'Filtrar' e 'Atendentes' (filtros client-side). */
import { Pencil, Plus } from 'lucide-react';
import type { Calendar, TeamMember } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/misc';
import { attendantColor } from './agenda-format';

export type AgendaTypeKey = 'commitments' | 'appointments' | 'absences';

const TYPE_ROWS: { key: AgendaTypeKey; label: string }[] = [
  { key: 'commitments', label: 'Compromissos' },
  { key: 'appointments', label: 'Apontamentos' },
  { key: 'absences', label: 'Ausências' },
];

function masterChecked(excludedCount: number, total: number): boolean | 'indeterminate' {
  if (excludedCount === 0) return true;
  if (total > 0 && excludedCount >= total) return false;
  return 'indeterminate';
}

function RowsSkeleton() {
  return (
    <div className="space-y-2 p-3" aria-busy>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-5/6" />
    </div>
  );
}

export function AgendaSidebar({
  calendars,
  calendarsPending,
  users,
  usersPending,
  excludedCalendarIds,
  excludedUserIds,
  excludedTypes,
  onToggleCalendar,
  onToggleAllCalendars,
  onToggleUser,
  onToggleAllUsers,
  onToggleType,
  onToggleAllTypes,
  onNewCalendar,
  onEditCalendar,
  showTypes = true,
}: {
  calendars: Calendar[];
  calendarsPending: boolean;
  users: TeamMember[];
  usersPending: boolean;
  excludedCalendarIds: ReadonlySet<number>;
  excludedUserIds: ReadonlySet<number>;
  excludedTypes: ReadonlySet<AgendaTypeKey>;
  onToggleCalendar: (id: number) => void;
  onToggleAllCalendars: () => void;
  onToggleUser: (id: number) => void;
  onToggleAllUsers: () => void;
  onToggleType: (key: AgendaTypeKey) => void;
  onToggleAllTypes: () => void;
  onNewCalendar: () => void;
  onEditCalendar: (calendar: Calendar) => void;
  /** spec 039: a aba de placas só tem compromissos — o filtro de tipo some */
  showTypes?: boolean;
}) {
  return (
    <div className="flex flex-col gap-4">
      {/* ── Agendas ─────────────────────────────────────────────────────── */}
      <section className="rounded-lg border bg-card shadow-xs">
        <header className="flex items-center gap-2 border-b border-divider px-3 py-2.5">
          <Checkbox
            checked={masterChecked(excludedCalendarIds.size, calendars.length)}
            onCheckedChange={onToggleAllCalendars}
            aria-label="Marcar/desmarcar todas as agendas"
          />
          <span className="flex-1 text-sm font-semibold">Agendas</span>
          <Button size="icon-xs" onClick={onNewCalendar} aria-label="Nova agenda">
            <Plus />
          </Button>
        </header>
        {calendarsPending ? (
          <RowsSkeleton />
        ) : calendars.length === 0 ? (
          <p className="px-3 py-3 text-sm text-muted-foreground">Nenhuma agenda</p>
        ) : (
          <ul className="p-1.5">
            {calendars.map((c) => (
              <li
                key={c.id}
                className="group flex h-8 items-center gap-2 rounded-md px-1.5 hover:bg-muted"
              >
                <Checkbox
                  checked={!excludedCalendarIds.has(c.id)}
                  onCheckedChange={() => onToggleCalendar(c.id)}
                  aria-label={`Mostrar agenda ${c.name}`}
                />
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: c.color }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-sm">{c.name}</span>
                <button
                  type="button"
                  onClick={() => onEditCalendar(c)}
                  aria-label={`Editar agenda ${c.name}`}
                  className="invisible cursor-pointer text-muted-foreground hover:text-foreground group-hover:visible"
                >
                  <Pencil className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Filtrar ─────────────────────────────────────────────────────── */}
      {showTypes ? (
        <section className="rounded-lg border bg-card shadow-xs">
          <header className="flex items-center gap-2 border-b border-divider px-3 py-2.5">
            <Checkbox
              checked={masterChecked(excludedTypes.size, TYPE_ROWS.length)}
              onCheckedChange={onToggleAllTypes}
              aria-label="Marcar/desmarcar todos os tipos"
            />
            <span className="flex-1 text-sm font-semibold">Filtrar</span>
          </header>
          <ul className="p-1.5">
            {TYPE_ROWS.map((row) => (
              <li key={row.key} className="flex h-8 items-center gap-2 rounded-md px-1.5 hover:bg-muted">
                <Checkbox
                  checked={!excludedTypes.has(row.key)}
                  onCheckedChange={() => onToggleType(row.key)}
                  aria-label={`Mostrar ${row.label.toLowerCase()}`}
                />
                <span className="min-w-0 flex-1 truncate text-sm">{row.label}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* ── Atendentes ──────────────────────────────────────────────────── */}
      <section className="rounded-lg border bg-card shadow-xs">
        <header className="flex items-center gap-2 border-b border-divider px-3 py-2.5">
          <Checkbox
            checked={masterChecked(excludedUserIds.size, users.length)}
            onCheckedChange={onToggleAllUsers}
            aria-label="Marcar/desmarcar todos os atendentes"
          />
          <span className="flex-1 text-sm font-semibold">Atendentes</span>
        </header>
        {usersPending ? (
          <RowsSkeleton />
        ) : users.length === 0 ? (
          <p className="px-3 py-3 text-sm text-muted-foreground">Nenhum atendente</p>
        ) : (
          <ul className="p-1.5">
            {users.map((u) => (
              <li key={u.id} className="flex h-8 items-center gap-2 rounded-md px-1.5 hover:bg-muted">
                <Checkbox
                  checked={!excludedUserIds.has(u.id)}
                  onCheckedChange={() => onToggleUser(u.id)}
                  aria-label={`Mostrar atendente ${u.name}`}
                />
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: attendantColor(u.id) }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-sm">{u.name}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
